import styled from '@emotion/styled'
import { useRouter } from 'found'
import { TFunction } from 'i18next'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useTypedDispatch, useTypedSelector } from '..'
import { fetchPunctualityPageData } from '../actions/punctuality'
import HeaderTitle from '../components/header/HeaderTitle'
import { Container, PageHeader } from '../components/page/PageComponents'
import PunctualitySection from '../components/punctuality/PunctualitySection'
import WagonSection from '../components/punctuality/WagonSection'
import { TabsMenu } from '../components/tabs-menu/TabsMenu'
import { TabMenuOption } from '../components/tabs-menu/TabsMenuButton'
import Close from '../icons/Close'
import moment from '../lib/moment-fi'
import { resetWagonState } from '../reducers/wagonSlice'
import { getPunctualityKey, nightModeSelector } from '../Selectors'
import { getColor } from '../Theme'
import type { TrainPunctuality } from '../types/Input'

const CloseIcon = styled.div`
  align-content: end;
  display: flex;
  margin-right: 16px;
`

type Props = {
  t: TFunction
  trainNumber: string
  punctuality: TrainPunctuality | undefined
  trainDate: string
  params: Array<any>
  nightMode: boolean
}

const PunctualityPage = (props: Props) => {
  const { t } = useTranslation()
  const dispatch = useTypedDispatch()
  const { router } = useRouter()
  const params = useTypedSelector((state) => state.found.match.params)
  const trainDate = useTypedSelector((state) => state.punctuality.selectedTrainDate)
  const trainNumber = useTypedSelector((state) => state.punctuality.selectedTrainNumber)
  const nightMode = useTypedSelector(nightModeSelector)
  const [menuSelection, setMenuSelection] = useState<TabMenuOption>('main')

  useEffect(() => {
    if (params && params[0]) {
      const [path] = params[0].split('?')
      const pathParameters = path.split('/')
      const trainDate = pathParameters[0]
      const trainNumber = pathParameters[1]

      if (trainDate && trainNumber) {
        dispatch(fetchPunctualityPageData(trainNumber, trainDate))
      }
    }
  }, [params, dispatch])

  const punctuality = useTypedSelector(
    (state) => state.punctuality.punctualityByTrain[getPunctualityKey(trainNumber, trainDate)]
  )

  const dateFormatted = () => {
    if (!punctuality) return ''
    const departureDate = moment(punctuality.departureDate)
    const lastRowDate = moment(
      punctuality.timeTableRows[punctuality.timeTableRows.length - 1].scheduledTime
    )
    if (departureDate.isSame(lastRowDate, 'day')) {
      return departureDate.format('DD.MM.')
    } else if (departureDate.isSame(lastRowDate, 'month')) {
      return `${departureDate.format('DD.')}-${lastRowDate.format('DD.MM.')}`
    } else {
      return `${departureDate.format('DD.MM.')}-${lastRowDate.format('DD.MM.')}`
    }
  }

  const header = (punctuality?: TrainPunctuality) =>
    punctuality
      ? `${punctuality.trainType} ${punctuality.trainNumber} 
          ${punctuality.timeTableRows[0].stationShortCode} - 
          ${punctuality.timeTableRows[punctuality.timeTableRows.length - 1].stationShortCode}
          ${dateFormatted()}`
      : ''

  return (
    <Container>
      <PageHeader
        onClick={() => {
          dispatch(resetWagonState())
          router.push('/search/punctuality')
        }}
        height="64px"
      >
        <HeaderTitle title={t('punctualityHeader')} subtitle={header(punctuality)} />
        <CloseIcon>
          <Close
            iconSize="normal"
            fillColor={getColor({ nightMode }, ['primaryBlue'], ['grayLight'])}
          />
        </CloseIcon>
      </PageHeader>
      <TabsMenu
        activeMenu={menuSelection}
        onChange={(menu) => setMenuSelection(menu)}
        textMain={t('timetable')}
        textSecondary={t('wagons.label')}
        enableSwipe={false}
      />
      {menuSelection === 'main' && <PunctualitySection router={router} />}
      {menuSelection === 'secondary' && <WagonSection />}
    </Container>
  )
}

export default PunctualityPage
